import {call, Effect, put, select, takeLatest} from "redux-saga/effects";
import {
    applyProductConfig,
    clearProductState,
    IUpdateConfigOption,
    ProductAction,
    setConfig,
    setConfigQueryString,
    setDefaultConfigQueryString,
    setProduct,
    setProductPrices,
    setProductPriceUpdating,
    setProfessionalsFromPrice
} from "./product.actions";
import {IProductConfig, IProductConfigOption, OmitConfig} from "@montana/models/dev";
import {IStoreState} from "@montana/store/store";
import {IProductContainerState} from '@montana/containers/product/product';
import {pushNotification} from "../notification/notification.actions";
import {NotificationLevel} from "../notification";
import {
    IColorModel,
    IOption,
    IPrice,
    IProduct,
    IProductDimension,
    IProductPriceDimension,
    IProductPrices,
    IProductPricesRequest,
} from '@montana/models/generated/cms.interface';
import {getUrlParams, IParamSet, removeTrailingSlash} from "@montana/helpers/location.helper";
import {fetchFromPrice, fetchPrices} from "@montana/services/product";
import {isDimensionConditionMet, isOptionConditionMet} from "@montana/helpers/product.helper";
import {isBrowser} from "@montana/helpers/browser.helper";
import {IPageContainerState} from "../page";
import {LoadingPageKind, ProductPageKind} from "@montana/pages/dynamic-page.component";
import {ISetPageIsTransitioningAction, PageActionTypes} from "../page/page.actions";
import {ISettingsContainerState} from "../settings/settings";
import {connectServiceFunctions} from "../base/base.sagas";
import {getTranslation} from "../translations/translations.sagas";
import {CUSTOMER_MODE} from "@montana/constants/customer-mode";
import {IDynamicSettingsContainerState} from "@montana/containers/dynamic-settings/dynamic-settings";

export function* mergeConfigIntoUrlParams(productConfig: IProductConfig) {
    const {colors}: IDynamicSettingsContainerState = (yield select((state: IStoreState) => state.dynamicSettings)) as unknown as IDynamicSettingsContainerState;
    const {dimensions = [], measurement, code} = productConfig;
    const {pathname, search} = location;

    // Filter away all the values relevant for the product state.
    let pairs: string[] = removeTrailingSlash(search).replace('?', '').split('&').filter(pair => !!pair);
    pairs = pairs
        .filter(pair => !pair.startsWith('product'))
        .filter(pair => !pair.startsWith('measurement'))
        .filter(pair => {
            const validParam = dimensions.find(dimension => dimension.seoFriendlyDimensionName === decodeURIComponent(pair).toLowerCase());
            return validParam && isDimensionConditionMet(validParam, dimensions);
        });

    // Mapping to query params.
    const productParam = `product=${code?.toLowerCase()}`;
    const measurementParams = measurement ? `measurement=${measurement?.toLowerCase()}` : undefined;
    const configParams = dimensions.filter(dimension => isDimensionConditionMet(dimension, dimensions))
        .map(dimension => {
            if (dimension.isColour) {
                const queryValue = dimension.seoFriendlyDisplayValue || getColorQueryValueFromDimensionSettingsValue(dimension.id || dimension.colorId || '', colors);
                return `${dimension.seoFriendlyDimensionName}=${queryValue}`;
            } else {

                return `${dimension.seoFriendlyDimensionName?.toLowerCase()}=${dimension.seoFriendlyDisplayValue?.toLowerCase()}`;
            }
        });

    const sortedParams = [
        ...(measurementParams ? [measurementParams] : []),
        ...pairs,
        ...configParams
    ].sort((a, b) => a < b ? -1 : a > b ? 1 : 0);

    pairs = [
        productParam,
        ...sortedParams
    ];

    const updatedSearch = pairs.length > 0 ? `?${pairs.join("&")}` : "";
    history.replaceState(history.state, "", `${pathname}${updatedSearch}`);

    // Set current queryString in store, to be used on post to basket calls - to set links.
    //const queryString = configParams.length > 0 ? `&${configParams.join("&")}` : "";

    yield put(setConfigQueryString(updatedSearch));
}

interface IVariationConfig {
    config?: IProductConfigOption[];
    measurement?: string;
    anyInvalidParams?: boolean;
    onlyDefaultParams?: boolean;
}

function calcConfigFromParams(params: IParamSet[], dimensions?: IProductDimension[], measurement?: string): IVariationConfig {
    let anyInvalidParams = false;
    let onlyDefaultParams = true;
    let config = dimensions?.map(dimension => {
        // Goes through queryParams to find a match on this dimension's type
        const queryValue = params.find(param => {
            return dimension.seoFriendlyDimensionName?.toLowerCase() === decodeURIComponent(param.key.toLowerCase());
        });

        // Goes through, to check if found type exists.
        const optionsAll: IOption[] = [];

        if (dimension.options) {
            // Convert params to something that looks like IProductConfigOption for the helper
            const tempConfig = params.map(p => ({
                seoFriendlyDimensionName: p.key,
                cylindoFeatureParameters: p.value,
                // We don't have all fields, but the helper handles missing ones
            })) as IProductConfigOption[];

            // @ts-ignore
            for (const [, value] of Object.entries(dimension.options)) {
                value.forEach(option => {
                    if (isOptionConditionMet(option, tempConfig, measurement)) {
                        optionsAll.push(option);
                    }
                })
            }
        }

        const validOption = optionsAll.find(dimension => {
            return dimension.seoFriendlyDisplayValue?.toLowerCase() === decodeURIComponent(queryValue?.value || '').toLowerCase();
        });

        // else value is not valid, and flag is set.
        if (queryValue && !validOption) {
            anyInvalidParams = true;
        }

        // if value wasn't valid, the first option from dimension is picked.
        const option = validOption || defaultOption(dimension.options!);

        // Track if we are only applying default options
        onlyDefaultParams = onlyDefaultParams && (!validOption || !queryValue);

        // Trimming options out of dimension before setting into config array
        const trimmedDimension = {...dimension};
        delete trimmedDimension.options;

        return {
            ...trimmedDimension,
            ...option,
        };
    });

    config = config?.filter(dimension => {
        return isDimensionConditionMet(dimension, config);
    });

    return {config, anyInvalidParams, onlyDefaultParams};
}

// Return the first option from the first property with a length
const defaultOption = (options: { [p: string]: IOption[] }): IOption | undefined => {
    for (const optionsKey in options) {
        if (options[optionsKey].length) {
            return options[optionsKey][0];
        }
    }
}

// Return the first option without conditions from a dimension's options
const getFirstOptionWithoutConditions = (options: { [p: string]: IOption[] }): IOption | undefined => {
    for (const optionsKey in options) {
        const optionWithoutConditions = options[optionsKey].find(opt => !opt.conditions || opt.conditions.length === 0);
        if (optionWithoutConditions) {
            return optionWithoutConditions;
        }
    }
    return undefined;
}

export function* onProductInit(product: IProduct, path: string): IterableIterator<Effect> {
    const cylindoSku = (product as any)?.cylindoSku;

    if (!product) {
        return;
    }

    try {
        const {dimensions, code, measurements} = product;

        // Getting all query parameters
        let queryParams = isBrowser ? getUrlParams() : getUrlParams(path);
        let measurement = queryParams?.find(p => p.key.toLowerCase() === 'measurement')?.value;
        queryParams = queryParams.filter(param => param.key !== 'measurement' && param.key !== 'product');

        const {config, onlyDefaultParams} = calcConfigFromParams(queryParams, dimensions, measurement);

        // Set measurement to correct formatted value or default to first measurement if none is present in url
        const formattedMeasurement = measurements?.find(m => m.seoFriendlyValue?.toLowerCase() === measurement?.toLowerCase());
        if (formattedMeasurement) {
            measurement = formattedMeasurement.seoFriendlyValue;
        } else if (measurements?.length) {
            measurement = measurements[0].seoFriendlyValue;
        } else {
            measurement = '';
        }

        // Since config is now set, the product can be stored in redux.
        // It's important to set this BEFORE config to avoid race conditions in getProductVariant saga
        yield put(setProduct(product));

        // Applies initial product config.
        yield put(applyProductConfig({
            code,
            cylindoSku,
            dimensions: config,
            measurement,
            isDefaultConfig: !!onlyDefaultParams,
        }));

        // Is set because bundles and multicolored products use the query params to figure out its properties when adding it to the basket.
        const defaultQueryString = yield call(calcDefaultDefaultQueryString, code, config, measurement);
        yield put(setDefaultConfigQueryString(defaultQueryString));

    } catch (e) {
        console.log('error', e);
        // TODO
    }
}

function* calcDefaultDefaultQueryString(code?: string, dimensions?: IProductConfigOption[], measurement?: string) {
    const {colors}: IDynamicSettingsContainerState = (yield select((state: IStoreState) => state.dynamicSettings)) as unknown as IDynamicSettingsContainerState;
    const defaultQueryParams: string[] = [];
    if (!!dimensions && dimensions.length) {
        dimensions.forEach(dimension => {
            if (dimension.isColour) {
                const queryValue = getColorQueryValueFromDimensionSettingsValue(dimension.id || '', colors);
                defaultQueryParams.push(`${dimension.seoFriendlyDimensionName}=${queryValue}`);
            } else {
                if (dimension.conditions?.length) {
                    if (measurement && dimension.conditions.includes(measurement)) {
                        defaultQueryParams.push(`${dimension.seoFriendlyDimensionName}=${dimension.seoFriendlyDisplayValue}`)
                    }
                } else {
                    defaultQueryParams.push(`${dimension.seoFriendlyDimensionName}=${dimension.seoFriendlyDisplayValue}`)
                }
            }
        })
    }
    if (measurement && measurement.length) {
        defaultQueryParams.push(`measurement=${measurement}`)
    }
    return `?product=${code}&${defaultQueryParams.join('&')}`
}

function getColorQueryValueFromDimensionSettingsValue(queryValue: string, colors?: IColorModel[]): string | undefined {
    const colourDimension = colors?.find(color => color?.colorId?.toLowerCase() === queryValue.toLowerCase() || color?.seoFriendlyDisplayValue?.toLowerCase() === queryValue.toLowerCase() || color?.displayValue?.toLowerCase() === queryValue.toLowerCase());

    if (!colourDimension) {
        return;
    }

    return colourDimension.seoFriendlyDisplayValue || colourDimension.colorId?.toLowerCase();
}

export function* onUpdateProductConfig(action: IUpdateConfigOption): IterableIterator<Effect> {
    try {
        const {dimension} = action;
        if (!dimension) {
            return;
        }

        const {
            config = {},
            dimensions: productDimensions = []
        }: IProductContainerState = (yield select((state: IStoreState) => state.product)) as unknown as IProductContainerState;
        const {dimensions = []} = config;

        let newArr = [...dimensions];
        const index = dimensions.findIndex((d: IProductConfigOption) => d.seoFriendlyDimensionName === dimension.seoFriendlyDimensionName);

        // Update or append the action=>dimension to the newArr dimensions
        if (index >= 0) {
            newArr.splice(
                index,
                1,
                dimension
            )
        } else {
            newArr.push(dimension);
        }

        // Remove conditional dimensions no longer meeting condition
        newArr = newArr.filter(d => {
            if (!d.condition && !d.invalidCondition) {
                return true;
            }
            if (d.condition) {
                return !!newArr.find(dd => dd.cylindoFeatureParameters === d.condition);
            }
            if (d.invalidCondition) {
                return !newArr.find(dd => dd.cylindoFeatureParameters === d.invalidCondition);
            }

        });

        // Conditional Dimensions
        const conditionalDimensions = productDimensions
            .filter(d => (!newArr.find(newArrItem => newArrItem.dimensionName === d.dimensionName)))
            .filter(d => {
                if (!d.condition && !d.invalidCondition) {
                    return false;
                }
                if (d.condition || d.invalidCondition) {
                    if (d.condition === dimension.cylindoFeatureParameters) {
                        return true;
                    }
                    if (d.invalidCondition && d.invalidCondition !== dimension.cylindoFeatureParameters) {
                        // condition to check if d.invalidCondition exist in newArr
                        return !newArr.find(newArrItem => newArrItem.cylindoFeatureParameters === d.invalidCondition);

                    }
                }
                return false;
            })
            .map(d => {
                const defaultOpt = (!!d.options && defaultOption(d.options)) ?? {};

                return {
                    dimensionName: d.dimensionName,
                    seoFriendlyDimensionName: d.seoFriendlyDimensionName,
                    translationDimensionName: d.translationDimensionName,
                    condition: d.condition,
                    invalidCondition: d.invalidCondition,
                    ...defaultOpt
                }
            });

        conditionalDimensions.forEach(cd => {
            const i = newArr.findIndex(d => d.dimensionName === cd.dimensionName);
            if (i >= 0) {
                newArr.splice(
                    i,
                    1,
                    cd
                )
            } else {
                newArr.push(cd);
            }
        });

        yield put(applyProductConfig({
            ...config,
            dimensions: newArr,
        }));

    } catch (e) {
        // TODO
    }
}

export function* onUpdateProductVariant(): IterableIterator<Effect> {
    try {
        const {
            dimensions,
            config,
            cylindoSku
        }: any = (yield select((state: IStoreState) => state.product)) as unknown as IProductContainerState;

        const params = config?.dimensions?.map((d: IProductConfigOption) => ({
            key: d.dimensionName || "",
            value: d.cylindoFeatureParameters || ""
        })) || [];
        const {config: productConfig} = calcConfigFromParams(params, dimensions);

        yield put(applyProductConfig({
            ...config,
            cylindoSku: cylindoSku,
            dimensions: productConfig,
        }));

    } catch (e) {
        // TODO
    }
}

export function* getProductVariant() {
    try {
        const {config, dimensions: productDimensions}: IProductContainerState = (yield select((state: IStoreState) => state.product));
        const {market, customerMode}: ISettingsContainerState = (yield select((state: IStoreState) => state.settings));

        if (!config || !config?.code) {
            return;
        }


        const toPriceDimension = (d: IProductConfigOption): IProductPriceDimension => ({
            dimensionName: d.dimensionName,
            displayValue: d.colorId || d.id,
            type: d.type,
            cylindoFeatureParameters: d.cylindoFeatureParameters,
        });

        // Calculate updated dimensions with fallbacks applied when conditions aren't met
        const updatedDimensions = config.dimensions
            ?.map(d => {
                // Check if condition matches using the shared helper
                const conditionMet = isOptionConditionMet(d, config.dimensions, config.measurement);

                if (conditionMet) return d;

                // Condition not met - try to find first option without conditions
                const productDimension = productDimensions?.find(pd => pd.dimensionName === d.dimensionName);
                if (productDimension?.options) {
                    const fallbackOption = getFirstOptionWithoutConditions(productDimension.options);
                    if (fallbackOption) {
                        // Return dimension with fallback option data
                        return {
                            ...d,
                            ...fallbackOption,
                        };
                    }
                }

                // No fallback available, exclude this dimension
                return null;
            })
            .filter((d): d is IProductConfigOption => d !== null);

        // Check if dimensions have changed (fallbacks were applied)
        const dimensionsChanged = JSON.stringify(config.dimensions) !== JSON.stringify(updatedDimensions);

        // If dimensions changed due to fallbacks, update the config state and URL
        if (dimensionsChanged && updatedDimensions) {
            const updatedConfig = {
                ...config,
                dimensions: updatedDimensions,
            };

            // Update state
            yield put(setConfig(updatedConfig));

            // Update URL to reflect the fallback dimensions
            yield call(mergeConfigIntoUrlParams, updatedConfig);
        }

        const mappedDimensions = updatedDimensions?.map(toPriceDimension);

        const request: IProductPricesRequest = {
            dimensions: mappedDimensions && mappedDimensions.length ? mappedDimensions : undefined,
            measurement: config.measurement || undefined,
        };

        yield put(setProductPriceUpdating(true));

        if (customerMode === CUSTOMER_MODE.PRIVATE) {
            const [cFetchPrices]: [OmitConfig<typeof fetchPrices>] = yield connectServiceFunctions(fetchPrices);
            const response: Response = yield call(cFetchPrices, config.code, request);
            const {json, ok} = response;

            if (!ok) {
                throw new Error();
            }

            const prices: IProductPrices = yield call(json.bind(response));

            if (market?.hidePrices) {
                prices.beforePrice = undefined;
                prices.beforePriceExcludingVat = undefined;
                prices.priceWithDiscount = undefined;
                prices.savedAmount = undefined;
            }

            if (prices) {
                yield put(setProductPrices(prices));
            }
        }

        if (customerMode === CUSTOMER_MODE.PROFESSIONAL) {
            const [cFetchFromPrice]: [OmitConfig<typeof fetchFromPrice>] = yield connectServiceFunctions(fetchFromPrice);
            const fromPriceResponse: Response = yield call(cFetchFromPrice, market?.marketId, config?.code.toLowerCase().includes("p-") ? config.code : `P-${config.code}`);
            const {json, ok} = fromPriceResponse;

            if (!ok) {
                throw new Error();
            }

            const fromPrice: IPrice = yield call(json.bind(fromPriceResponse));

            if (fromPrice) {
                yield put(setProfessionalsFromPrice(fromPrice));
            }
        }

        yield put(setProductPriceUpdating(false));
    } catch (e) {
        const fallbackMessage = "Der opstod en fejl, prisen kunne ikke udregnes for dette produkt";
        const errorMessage: string = yield getTranslation((t) => t.basket.getBasketError, fallbackMessage);

        yield put(pushNotification(errorMessage, NotificationLevel.ERROR));

        yield put(setProductPrices({visibleNotBuyable: true} as IProductPrices)); // RESET Prices
        yield put(setProductPriceUpdating(false));
    }
}

export function* onApplyProductConfigToUrl() {
    const {kind}: IPageContainerState = (yield select((state: IStoreState) => state.page)) as unknown as IPageContainerState;
    const {config = {}}: IProductContainerState = (yield select((state: IStoreState) => state.product)) as unknown as IProductContainerState;

    // We do not want to the touch the url product-wise on the server and only when we are actually on a product page.
    if (!!isBrowser && kind === ProductPageKind && !config.isDefaultConfig) {
        try {
            yield call(mergeConfigIntoUrlParams, config);
        } catch (e) {
            console.error('Failed to merge config params into url.');
        }
    }
}

export function* onPageData(action: ISetPageIsTransitioningAction) {
    if (action.isTransitioning) {
        return;
    }

    const {kind}: IPageContainerState = yield select((s: IStoreState) => s.page);

    if (kind !== ProductPageKind && kind !== LoadingPageKind) {
        yield put(clearProductState());
    }
}

export const productSagas = [
    takeLatest(ProductAction.APPLY_CONFIG, onApplyProductConfigToUrl),
    takeLatest(ProductAction.APPLY_CONFIG, getProductVariant),
    takeLatest(ProductAction.UPDATE_VARIANT, onUpdateProductVariant),
    takeLatest(ProductAction.UPDATE_CONFIG, onUpdateProductConfig),
    takeLatest(PageActionTypes.FETCH_DATA_SUCCESS, onApplyProductConfigToUrl), // CSR: Triggers on fetch data success, since page kind is needed
    takeLatest(PageActionTypes.SET_TRANSITIONING, onPageData),
];
